import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { HelpLink } from '../components/HelpLink';
import { Icon } from '../components/Icon';
import { usePublicAccess } from '../features/public/publicAccess';
import { AccountMenu } from './workspace/AccountMenu';
import { BrandLogo } from './workspace/BrandLogo';
import { TenantSwitcher } from '../tenancy/TenantSwitcher';

/**
 * #959 (owner): one header bar for every page outside the drawing workspace -- the public pages,
 * Monographs and other object pages, your profile and comments, the viewers and composers, and
 * the sign-in screen. It is the workspace's own blue bar (brand, Profile | Draw, Help, account),
 * and it stays in place while the page scrolls, as the workspace's does.
 */
export function SiteHeader({ showAccount = true }: { readonly showAccount?: boolean }) {
  const { t } = useTranslation();
  const { signedIn, signIn, signUp, signInError } = usePublicAccess();
  const location = useLocation();
  const here = location.pathname + location.search;

  return (
    <header className="workspace-topbar site-header" data-testid="site-header">
      <Link to={signedIn ? '/' : '/public'} className="workspace-brand" aria-label={t('workspace.brand')}>
        <BrandLogo className="workspace-brand-mark" />
        <span aria-hidden="true">{t('workspace.brand')}</span>
      </Link>
      {signedIn ? (
        <nav className="workspace-nav" aria-label={t('appNav.label')}>
          <NavLink to="/" end>
            {t('appNav.profile')}
          </NavLink>
          <NavLink to="/draw" end>
            {t('appNav.draw')}
          </NavLink>
        </nav>
      ) : null}
      <div className="workspace-topbar-actions site-header-actions">
        <HelpLink />
        {showAccount && signedIn ? <TenantSwitcher /> : null}
        {showAccount && signedIn ? <AccountMenu /> : null}
        {showAccount && !signedIn ? (
          <>
            <button type="button" className="site-header-secondary" onClick={() => signUp(here)}>
              {t('publicPages.signUp')}
            </button>
            <button type="button" className="button-with-icon site-header-sign-in" onClick={() => signIn(here)}>
              <Icon name="accountCircle" />
              <span>{t('publicPages.signIn')}</span>
            </button>
          </>
        ) : null}
      </div>
      {showAccount && !signedIn && signInError ? (
        <p className="site-header-auth-error" role="alert">
          {signInError}
        </p>
      ) : null}
    </header>
  );
}
